import { Button, Chip, Dialog, DialogContent, DialogContentText, DialogTitle, Divider, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { createFilterOptions } from "@mui/material/Autocomplete";
import ResponsiveSelect from "../../../components/ui/ResponsiveSelect";
import { makeStyles } from "@/compat/muiStyles";
import { HelpOutline, NotificationsActiveOutlined, NotificationsNoneOutlined } from "@mui/icons-material";
import { Autocomplete } from "@/compat/Autocomplete";
import { validEmailRegex } from "../../utils/RegexUtils";
import { getAcknowledgementStatusDescription } from "../../../constants/acknowledgementStatusConstants";

// AcknowledgementStatus enum: Sent=0, Viewed=1, Acknowledged=2.
const ACKNOWLEDGED = 2;

const useStyles = makeStyles((theme) => ({
    subTitle: {
        padding: theme.spacing(3),
    },
    title: {
        padding: theme.spacing(0),
        margin: theme.spacing(0),
    },
    dialogContent: {
        display: "flex",
        gap: theme.spacing(2),
        alignItems: "flex-start",
        marginBottom: theme.spacing(2),
    },
    error: {
        "&.MuiChip-outlined": {
            border: "1px solid red",
            color: "red",
        },
    },
    inviteButton: {
        // Fixed width so "Add" and "Invite" match, and no top margin so both sit
        // flush with the top of the input beside them.
        minWidth: 96,
        flexShrink: 0,
    },
    sharedWithHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
    },
    emails: {
        display: "flex",
        alignItems: "center",
    },
    chip: {
        marginLeft: "auto",
        fontSize: "0.75rem",
        marginRight: theme.spacing(1),
    },
    groupMessage: {
        display: "block",
        marginTop: theme.spacing(-1),
        marginBottom: theme.spacing(2),
    },
    groupHelpIcon: {
        marginTop: theme.spacing(1.5),
        cursor: "help",
        width: 20,
        flexShrink: 0,
    },
    helpSpacer: {
        width: 20,
        flexShrink: 0,
    },
    selectAllOption: { fontWeight: 600 },
    emailList: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
}));

const SETUP_PATH = "Template Administration → Risk Assessments Setup → Share groups";
const SELECT_ALL = "__select_all__";

// A recipient can be either a site contact (picked from the dropdown) or a
// free-typed email string. Both ultimately resolve to an email address.
const toEmail = (item) => (typeof item === "string" ? item.trim() : item?.email);
const contactName = (c) => `${c?.firstName ?? ""} ${c?.lastName ?? ""}`.trim();

// Let the user search the site-user list by name OR email as they type.
const filterContacts = createFilterOptions<any>({
    stringify: (option) => `${option.firstName ?? ""} ${option.lastName ?? ""} ${option.email ?? ""}`,
});

export default function ShareRiskAssessmentDialog({
    emailList,
    shareDialogOpen,
    closeShareDialog,
    riskAssessmentHistoryAcknowledgementRecipients,
    shareRiskAssessmentHistoryInvite,
    remindRecipients,
    setEmailList,
    siteContacts,
    shareGroups = [],
    accountContacts = {},
    canManageShareGroups = false,
}) {
    const classes = useStyles();

    const [selectedGroupId, setSelectedGroupId] = useState<any>("");
    const [groupMessage, setGroupMessage] = useState("");

    const groupHelpText = canManageShareGroups
        ? `Share groups are reusable sets of recipients. Create or edit them in ${SETUP_PATH}.`
        : "Share groups are reusable sets of recipients, set up by a template administrator for your account.";

    const recipients = riskAssessmentHistoryAcknowledgementRecipients ?? [];
    const recipientEmails = new Set(recipients.map((r) => r.email));
    // Everyone who hasn't acknowledged yet (Sent or Viewed) is "pending".
    const pendingEmails = recipients.filter((r) => r.status !== ACKNOWLEDGED).map((r) => r.email);

    // Site users who have an email address and aren't already recipients.
    const contactOptions = (siteContacts ?? []).filter((c) => c?.email && !recipientEmails.has(c.email));

    const selectedEmails = new Set(emailList.map(toEmail));
    const addableContacts = contactOptions.filter((c) => !selectedEmails.has(c.email));
    const recipientOptions =
        addableContacts.length > 1 ? [{ externalId: SELECT_ALL }, ...contactOptions] : contactOptions;

    const onRecipientsChange = (value) => {
        if (value.some((v) => v?.externalId === SELECT_ALL)) {
            setEmailList([...emailList, ...addableContacts]);
            return;
        }
        setEmailList([...value]);
    };

    const resolvedEmails = emailList.map(toEmail);
    const hasInvalidEmail = emailList.some((item) => typeof item === "string" && !item.trim().match(validEmailRegex));
    const hasExistingRecipient = resolvedEmails.some((email) => recipientEmails.has(email));
    const inviteDisabled = emailList.length === 0 || hasInvalidEmail || hasExistingRecipient;

    // Members held as a contact id resolve live, so a leaver is skipped not emailed.
    const addSelectedGroup = () => {
        const group = shareGroups.find((g) => g.id === selectedGroupId);
        if (!group) return;

        const taken = new Set([...resolvedEmails, ...recipientEmails]);
        const additions = [];
        let unresolved = 0;
        let alreadyThere = 0;

        (group.members ?? []).forEach((member) => {
            const contact = member.contactExternalId ? accountContacts?.[member.contactExternalId] : null;
            const email = member.contactExternalId ? contact?.email : member.email;

            if (!email) {
                unresolved += 1;
                return;
            }
            if (taken.has(email)) {
                alreadyThere += 1;
                return;
            }
            taken.add(email);
            additions.push(contact ?? email);
        });

        if (additions.length) setEmailList([...emailList, ...additions]);

        const parts = [`${additions.length} added`];
        if (alreadyThere) parts.push(`${alreadyThere} already invited`);
        if (unresolved)
            parts.push(`${unresolved} no longer ${unresolved === 1 ? "an active user" : "active users"} (skipped)`);
        setGroupMessage(parts.join(" · "));
        setSelectedGroupId("");
    };

    return (
        <Dialog open={shareDialogOpen} onClose={closeShareDialog} fullWidth maxWidth="sm">
            <div className={classes.subTitle}>
                <DialogTitle className={classes.title}>Share Risk Assessment</DialogTitle>
                <DialogContentText className={classes.title}>
                    Invite anyone to review this risk assessment. Recipients do not need a SafetyNest account &mdash; add colleagues from the list or type any email address.
                </DialogContentText>
            </div>
            <DialogContent>
                {shareGroups.length > 0 && (
                    <>
                        <div className={classes.dialogContent}>
                            <ResponsiveSelect
                                size="small"
                                fullWidth
                                label="Add from a share group"
                                options={shareGroups}
                                optionValueKey="id"
                                optionLabelKey="name"
                                value={selectedGroupId}
                                onChange={(e: any) => setSelectedGroupId(e.target.value)}
                            />
                            <Tooltip title={groupHelpText}>
                                <HelpOutline className={classes.groupHelpIcon} fontSize="small" color="disabled" />
                            </Tooltip>
                            <Button
                                className={classes.inviteButton}
                                variant="outlined"
                                color="primary"
                                disabled={!selectedGroupId}
                                onClick={addSelectedGroup}
                            >
                                Add
                            </Button>
                        </div>
                        {groupMessage && (
                            <Typography variant="caption" color="textSecondary" className={classes.groupMessage}>
                                {groupMessage}
                            </Typography>
                        )}
                    </>
                )}
                {shareGroups.length === 0 && (
                    <Typography variant="caption" color="textSecondary" className={classes.groupMessage}>
                        {canManageShareGroups
                            ? `Tip: save time on future shares by setting up a share group in ${SETUP_PATH}.`
                            : "Tip: share groups let you share to the same set of people in one click. Ask a template administrator for your account to set one up under Risk Assessments Setup."}
                    </Typography>
                )}
                <div className={classes.dialogContent}>
                    <Autocomplete
                        fullWidth
                        multiple
                        freeSolo
                        disableCloseOnSelect
                        filterSelectedOptions
                        id="share-recipients"
                        options={recipientOptions}
                        value={emailList}
                        onChange={(e, value) => onRecipientsChange(value)}
                        filterOptions={filterContacts}
                        getOptionLabel={(option) =>
                            typeof option === "string"
                                ? option
                                : option.externalId === SELECT_ALL
                                    ? "Select all"
                                    : contactName(option) || option.email
                        }
                        getOptionKey={(option) => (typeof option === "string" ? option : option.externalId)}
                        isOptionEqualToValue={(option, value) => toEmail(option) === toEmail(value)}
                        renderOption={(option) =>
                            option.externalId === SELECT_ALL ? (
                                <Typography variant="body2" color="primary" className={classes.selectAllOption}>
                                    Select all ({addableContacts.length})
                                </Typography>
                            ) : (
                                <div>
                                    <Typography variant="body2">{contactName(option) || option.email}</Typography>
                                    {contactName(option) && (
                                        <Typography variant="caption" color="textSecondary">
                                            {option.email}
                                        </Typography>
                                    )}
                                </div>
                            )
                        }
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                                const email = toEmail(option);
                                const label = typeof option === "string" ? option : contactName(option) || option.email;
                                const invalid = (typeof option === "string" && !option.trim().match(validEmailRegex)) || recipientEmails.has(email);
                                const { key, ...tagProps } = getTagProps({ index });
                                return (
                                    <Chip
                                        key={key}
                                        {...tagProps}
                                        classes={invalid ? { outlined: classes.error } : undefined}
                                        variant="outlined"
                                        label={label}
                                        title={email}
                                    />
                                );
                            })
                        }
                        renderInput={(params) => (
                            <TextField {...params} variant="outlined" label="Add people" placeholder="Type a name or email…" />
                        )}
                    />
                    {/* Matches the group row's help icon so both fields end level. */}
                    <span className={classes.helpSpacer} aria-hidden="true" />
                    <Button
                        className={classes.inviteButton}
                        variant="contained"
                        color="primary"
                        disabled={inviteDisabled}
                        onClick={() => shareRiskAssessmentHistoryInvite([...new Set(resolvedEmails)], false)}
                    >
                        Invite
                    </Button>
                </div>
                <Divider />
                <div className={classes.sharedWithHeader}>
                    <Typography variant="body2">Shared with:</Typography>
                    {pendingEmails.length > 0 && (
                        <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            startIcon={<NotificationsActiveOutlined />}
                            onClick={() => remindRecipients(pendingEmails)}
                        >
                            Remind all pending
                        </Button>
                    )}
                </div>
                {recipients.length === 0 && (
                    <Typography color="textSecondary" variant="caption">
                        Add people above to share this risk assessment.
                    </Typography>
                )}
                <div className={classes.emailList}>
                    {recipients.map((x) => (
                        <div className={classes.emails} key={x.id}>
                            <Typography variant="caption">{x.email}</Typography>
                            <Chip className={classes.chip} label={getAcknowledgementStatusDescription(x.status)} variant="outlined" size="small" />
                            {x.status !== ACKNOWLEDGED && (
                                <Tooltip title="Send reminder">
                                    <IconButton size="small" onClick={() => remindRecipients([x.email])}>
                                        <NotificationsNoneOutlined fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </div>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
}
